import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";
import { useMyTeams } from "../../lib/react-query/teams";
import { useTransferProject } from "../../lib/react-query/projects";
import { CustomModal, StaticSelect, FormError, showToast, Spinner } from "../shared";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${parseFloat((bytes / 1024 ** i).toFixed(1))} ${units[i]}`;
};

const fetchProjectFileUsage = async (projectId) => {
  const { data } = await axiosInstance.get(endPoints.ProjectFilesUsage(projectId));
  return data?.data?.used_bytes || 0;
};

const MoveProjectModal = ({ isOpen, onClose, project, currentTeamId }) => {
  const { data: teamsData, isLoading: teamsLoading } = useMyTeams(isOpen);
  const teams = useMemo(
    () => (Array.isArray(teamsData) ? teamsData : teamsData?.results || []),
    [teamsData],
  );
  const { mutateAsync: transferProject, isPending } = useTransferProject();
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [error, setError] = useState(null);

  const { data: projectSize = 0, isLoading: sizeLoading } = useQuery({
    queryKey: queryKeys.ProjectFilesUsage(project?.id),
    queryFn: () => fetchProjectFileUsage(project.id),
    enabled: !!project?.id && isOpen,
  });

  const destinationOptions = useMemo(() => {
    if (!Array.isArray(teams)) return [];
    return teams
      .filter((t) => t?.id !== currentTeamId)
      .map((t) => ({
        id: String(t.id),
        name: `${t.name}${t.is_personal ? " (Personal)" : ""}`,
      }));
  }, [teams, currentTeamId]);

  const selectedTeam = useMemo(() => {
    return teams.find((t) => String(t.id) === selectedTeamId);
  }, [teams, selectedTeamId]);

  const wouldExceedQuota = useMemo(() => {
    if (!selectedTeam) return false;
    const used = selectedTeam.storage_used_bytes || 0;
    const quota = selectedTeam.storage_quota_bytes || 0;
    return used + projectSize > quota;
  }, [selectedTeam, projectSize]);

  const handleClose = () => {
    setSelectedTeamId("");
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedTeamId) {
      setError("Please select a destination workspace.");
      return;
    }
    setError(null);
    try {
      await transferProject({
        projectId: project.id,
        team_id: Number(selectedTeamId),
        fromTeamId: currentTeamId,
      });
      showToast("Project moved successfully", "success", { position: "top-center" });
      handleClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        "Failed to move project.";
      setError(msg);
    }
  };

  return (
    <CustomModal
      title="Move project to another workspace"
      open={isOpen}
      close={handleClose}
      primaryButtonText="Move project"
      primaryButtonClick={handleConfirm}
      primaryButtonLoading={isPending}
      primaryButtonDisabled={
        !selectedTeamId || wouldExceedQuota || isPending || sizeLoading
      }
      secondaryButtonText="Cancel"
      secondaryButtonClick={handleClose}
      isDismissable={false}
    >
      <div className="flex flex-col gap-4 text-sm text-darkgrey">
        <p>
          Move <strong>{project?.title}</strong> to a different workspace.
          Project members and files will move with it.
        </p>

        <StaticSelect
          label={
            <span className="inline-flex items-center gap-2">
              Destination workspace
              {sizeLoading && <Spinner className="text-secondary-dark" size="sm" />}
            </span>
          }
          name="destinationTeam"
          placeholder="Choose a workspace..."
          options={destinationOptions}
          value={selectedTeamId}
          onChange={(e) => setSelectedTeamId(e.target.value)}
          disabled={teamsLoading || sizeLoading}
          error={wouldExceedQuota}
          errorMsg={
            wouldExceedQuota
              ? "The destination workspace does not have enough free storage for this project."
              : undefined
          }
          description={
            selectedTeam
              ? `Storage: ${formatBytes(selectedTeam.storage_used_bytes)} of ${formatBytes(
                  selectedTeam.storage_quota_bytes
                )} used${projectSize > 0 ? ` · project size ${formatBytes(projectSize)}` : ""}`
              : undefined
          }
        />

        <FormError message={error} />
      </div>
    </CustomModal>
  );
};

export default MoveProjectModal;
